import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedLanguageService } from '@language';
import {
    ActionItem,
    ApiFragment,
    DialogConfig,
    IconSize,
    MediaType,
    PreviewStatus,
    Toast
} from '@stylehaven/components';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToastGroupGuard } from '../../../common/entities/common/toast-group.entity';
import { StoreService } from '../../../common/services/store/store.service';

@Component({
    selector: 'med-fragment-preview-dialog',
    templateUrl: './fragment-preview-dialog.component.html',
    styleUrls: ['./fragment-preview-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FragmentPreviewDialogComponent implements OnInit {
    readonly iconSize: IconSize = IconSize.SMALL;

    internalConfig!: DialogConfig;

    isLoading!: boolean;
    showImageView!: boolean;
    isDocument: boolean = false;

    placeholderMessage!: string;
    indexTitle!: string;
    contextRecordName!: string;

    toasts$: Observable<Toast[]>;

    public recordActions: ActionItem[] | undefined;

    private toastService: StoreService = inject(StoreService);

    @Input() set config(config: DialogConfig) {
        if (config) {
            this.internalConfig = config;
            this.ngOnInit();
            this.init();
        }
    }

    @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
    @HostListener('document:keydown.arrowRight', ['$event'])
    onArrowRight(event: KeyboardEvent) {
        event.stopImmediatePropagation();
        event.preventDefault();

        if (this.internalConfig?.data.nextNav) {
            this.router.navigate(this.internalConfig.data.nextNav, {
                relativeTo: this.internalConfig.data.route,
                state: this.internalConfig.data.state
            });
        } else if (this.internalConfig?.data.nextAction) {
            this.internalConfig.data.nextAction();
        }
    }

    @HostListener('document:keydown.arrowLeft', ['$event'])
    onArrowLeft(event: KeyboardEvent) {
        event.stopImmediatePropagation();
        event.preventDefault();

        if (this.internalConfig?.data.prevNav) {
            this.router.navigate(this.internalConfig.data.prevNav, {
                relativeTo: this.internalConfig.data.route,
                state: this.internalConfig.data.state
            });
        } else if (this.internalConfig?.data.prevAction) {
            this.internalConfig.data.prevAction();
        }
    }

    // Injectors

    private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

    constructor(
        private readonly translateService: SharedLanguageService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        this.recordActions =
            this.internalConfig.data.recordActions?.map((action: ActionItem) => {
                if (Object.prototype.hasOwnProperty.call(action, 'navigationLink')) {
                    return {
                        ...action,
                        navigationLink: {
                            ...action.navigationLink,
                            route: action.navigationLink!.route,
                            extras: {
                                ...action.navigationLink!.extras,
                                relativeTo: this.activatedRoute.parent
                            }
                        }
                    };
                }
                return action;
            }) ?? undefined;
    }

    // #region Getters
    get contextPageRecordTitle(): string {
        return this.internalConfig.data.contextPageRecordTitle;
    }

    get record(): ApiFragment | undefined {
        return this.internalConfig.data.record;
    }

    get recordTitle(): string {
        return this.record?.getTitle() ?? '';
    }

    get previewStatus(): PreviewStatus {
        return this.internalConfig.data.previewStatus;
    }

    get paginatedResults() {
        return this.internalConfig.data.paginatedResults;
    }

    get keyframes() {
        return this.internalConfig.data.allKeyframes;
    }

    get keyframeIndex() {
        return this.internalConfig.data.keyframeIndex;
    }

    get fragmentIndex(): number {
        return this.internalConfig.data.fragmentIndex;
    }

    get prevNav() {
        return this.internalConfig.data.prevNav;
    }

    get nextNav() {
        return this.internalConfig.data.nextNav;
    }

    get route() {
        return this.internalConfig.data.route;
    }

    get detailUrl() {
        return this.record?.getDetailUrl();
    }
    // #endregion

    // #region Init
    private init(): void {
        this.isLoading = this.previewStatus === PreviewStatus.Processing;

        this.showImageView = !(
            this.previewStatus === PreviewStatus.Error ||
            this.previewStatus === PreviewStatus.Processing ||
            this.previewStatus === PreviewStatus.NoPreview
        );
        if (!this.showImageView) this.placeholderMessage = this.getPlaceHolderText();

        if (this.paginatedResults || this.keyframes) this.setIndexTitle();

        this.initToastsStream();

        this.isDocument = this.record?.getType() === MediaType.document && this.record.hasKeyFrames();
    }

    private setIndexTitle(): void {
        if (this.paginatedResults) {
            const resultsPerPage = this.paginatedResults.paginationInfo.resultsPerPage;
            const page = this.paginatedResults.paginationInfo.page;
            const resultNumber = page * resultsPerPage + this.fragmentIndex + 1;
            const totalResults = this.paginatedResults.paginationInfo.totalNumberOfResults;
            this.contextRecordName = this.contextPageRecordTitle;

            if (this.contextRecordName) {
                this.indexTitle = this.translateService.translate('components.pagination.index-title-within', {
                    result: resultNumber,
                    totalResults: totalResults
                });
            } else {
                this.indexTitle = this.translateService.translate('components.pagination.index-title', {
                    result: resultNumber,
                    totalResults: totalResults
                });
            }
        } else if (this.keyframes) {
            const keyframeIndex = this.keyframeIndex;
            this.indexTitle = this.translateService.translate('components.pagination.index-title', {
                result: keyframeIndex + 1,
                totalResults: this.keyframes.length
            });
        } else {
            this.indexTitle = '';
        }
    }

    private getPlaceHolderText(): string {
        switch (this.previewStatus) {
            case PreviewStatus.Processing:
                return this.translateService.translate('overrides.RecordStatus.Processing');
            case PreviewStatus.Error:
                return this.translateService.translate('overrides.RecordStatus.Rejected');
            default:
                return '';
        }
    }
    // #endregion

    // #region Toasts
    // Needed to display toasts on top of fullscreen dialog
    private initToastsStream(): void {
        this.toasts$ = this.toastService.getToasts();
    }

    public closeToast(id: number): void {
        this.toasts$.pipe(take(1)).subscribe(toasts => {
            const targetToast: Toast | undefined = toasts.find(toast => toast.id === id);
            if (targetToast && ToastGroupGuard(targetToast)) {
                targetToast.childToasts.forEach(toast => this.toastService.deleteToast(toast.id));
            } else {
                this.toastService.deleteToast(id);
            }
        });
    }
    // #endregion

    // #region Events
    navigateToDetailPage(): void {
        this.closeModal.next();
        setTimeout(
            () =>
                this.router.navigate([this.detailUrl], {
                    state: this.internalConfig.data.state
                }),
            10
        );
    }
    // #endregion
}
